import Header1 from "../../../components/header1/Header1";
import Footer from "../../../components/footer/Footer";
import React, { useState, useEffect } from "react";
import {
  fetchApprovedEmails,
  addBulkEmails,
  deleteEmail,
} from "../../../services/studentportalapis";

const ApprovedEmailsManager = () => {
  const [emailsByCollege, setEmailsByCollege] = useState({});
  const [bulkEmails, setBulkEmails] = useState("");
  const [collegeId, setCollegeId] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); 
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const emailsPerPage = 10;

  // Fetch approved emails grouped by collegeId
  useEffect(() => {
    fetchApprovedEmails()
      .then((data) => {
        console.log("Fetched emails:", data);
        setEmailsByCollege(data); 
      })
      .catch((err) => {
        console.error("Error fetching emails:", err);
        setError("Failed to fetch emails.");
      });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleAddBulkEmails = () => {
    if (!bulkEmails.trim() || !collegeId.trim()) {
      setError("Please enter both College ID and Emails.");
      return;
    }

    const emailArray = bulkEmails
      .split(/[,\n;]+/)
      .map((email) => email.trim())
      .filter((email) => email !== "");

    const invalidEmails = emailArray.filter((email) => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email(s): ${invalidEmails.join(", ")}`);
      return;
    }

    if (emailArray.length === 0) {
      setError("No valid emails found to add.");
      return;
    }

    addBulkEmails(emailArray, collegeId)
      .then((data) => {
        console.log("API Response:", data);

        if (data.success) {
          setEmailsByCollege((prev) => ({
            ...prev,
            [collegeId]: [...(prev[collegeId] || []), ...emailArray],
          }));
          setBulkEmails("");
          setSuccess(data.message);
          setError("");
        } else {
          setError("Failed to add emails. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error adding emails:", error);
        setError("Failed to add emails. Please try again.");
      });
  };

  const handleDeleteEmail = (email, collegeId) => {
    deleteEmail(email, collegeId)
      .then(() => {
        setEmailsByCollege((prev) => ({
          ...prev,
          [collegeId]: prev[collegeId].filter((e) => e !== email),
        }));
        setSuccess("Email deleted successfully.");
        setError("");
      })
      .catch(() => setError("Failed to delete email. Please try again."));
  };

  const filteredEmails = Object.entries(emailsByCollege)
    .flatMap(([collegeId, emails]) => emails.map((email) => ({ email, collegeId })))
    .filter((item) => item.email.toLowerCase().includes(searchQuery.toLowerCase()));

  const indexOfLastEmail = currentPage * emailsPerPage;
  const indexOfFirstEmail = indexOfLastEmail - emailsPerPage;
  const currentEmails = filteredEmails.slice(indexOfFirstEmail, indexOfLastEmail);
  const totalPages = Math.ceil(filteredEmails.length / emailsPerPage);

  return (
    <div className="md:mx-20">
      <Header1 />
      <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Approved Emails Manager
        </h1>

        {error && <div className="bg-red-100 text-red-700 p-4 mb-4 rounded">{error}</div>}
        {success && <div className="bg-green-100 text-green-700 p-4 mb-4 rounded">{success}</div>}

        {/* Add Bulk Emails */}
        <div className="mb-6">
          <input
            type="text"
            value={collegeId}
            onChange={(e) => setCollegeId(e.target.value)}
            placeholder="Enter College ID"
            className="border p-2 rounded w-full mb-3"
          />
          <textarea
            value={bulkEmails}
            onChange={(e) => setBulkEmails(e.target.value)}
            placeholder="Enter emails separated by commas or new lines"
            className="border p-2 rounded w-full h-24"
          ></textarea>
          <button
            onClick={handleAddBulkEmails}
            className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
          >
            Add Emails
          </button>
        </div>

        {/* Search Emails */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search emails"
          className="border p-2 rounded w-full mb-4"
        />

        {/* Display Emails */}
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2">Email</th>
              <th className="border p-2">College ID</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentEmails.map(({ email, collegeId }) => (
              <tr key={email}>
                <td className="border p-2">{email}</td>
                <td className="border p-2">{collegeId}</td>
                <td className="border p-2">
                  <button
                    onClick={() => handleDeleteEmail(email, collegeId)}
                    className="bg-red-500 text-white px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-between mt-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            className={`px-4 py-2 rounded ${currentPage > 1 ? "bg-gray-300" : "bg-gray-100 cursor-not-allowed"}`}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            className={`px-4 py-2 rounded ${currentPage < totalPages ? "bg-gray-300" : "bg-gray-100 cursor-not-allowed"}`}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApprovedEmailsManager;
